import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetHintBar, CNetRow, CNetActions, CNetResult, G } from './_cnet';

// Density units → kg/m^3
const DENSITY = {
  'kilogram/cubic meter [kg/m³]': 1,
  'gram/cubic centimeter [g/cm³]': 1000,
  'kilogram/cubic centimeter [kg/cm³]': 1_000_000,
  'gram/cubic meter [g/m³]': 0.001,
  'pound/cubic foot [lb/ft³]': 16.0184634,
  'pound/cubic inch [lb/in³]': 27679.9047,
  'ounce/cubic inch [oz/in³]': 1729.994,
};
// Volume units → cubic meter
const VOLUME = {
  'cubic meter [m³]': 1,
  'cubic centimeter [cm³]': 1e-6,
  'cubic millimeter [mm³]': 1e-9,
  'liter [L]': 1e-3,
  'milliliter [mL]': 1e-6,
  'cubic foot [ft³]': 0.0283168466,
  'cubic inch [in³]': 1.6387064e-5,
  'cubic yard [yd³]': 0.7645548580,
  'gallon (US) [gal]': 0.00378541178,
  'gallon (UK) [gal]': 0.00454609,
  'barrel (oil) [bbl]': 0.158987294928,
};

// Mass conversions from kilogram
const MASS_UNITS = [
  ['kilogram', 1],
  ['gram', 1000],
  ['milligram', 1_000_000],
  ['ton', 0.001],
  ['pound (lb)', 2.20462262185],
  ['carrat', 5000],
  ['grain', 15432.3583529],
  ['atomic mass unit', 6.02214076e26],
];

export default function MassCalc() {
  const [density, setDensity] = useState(8900);
  const [dUnit, setDUnit] = useState('kilogram/cubic meter [kg/m³]');
  const [volume, setVolume] = useState(1);
  const [vUnit, setVUnit] = useState('cubic meter [m³]');
  const [res, setRes] = useState({ kg: 8900, showSteps: true });

  const calc = () => {
    const d = Number(density) * DENSITY[dUnit];
    const v = Number(volume) * VOLUME[vUnit];
    const kg = d * v;
    setRes({ kg, showSteps: true, dLabel: dUnit, vLabel: vUnit });
  };
  const clear = () => { setDensity(''); setVolume(''); setRes({ kg: null }); };

  return (
    <CalcShell look="cnet" showActions={false} title="Mass Calculator" subtitle="This is a basic mass calculator based on density and volume. This calculator takes and generates results of many common units.">
      <CNetHintBar />
      <div className="cnet-panel">
        <CNetRow label="Density">
          <input data-testid="mass-density" className="cnet-input" type="number" value={density} onChange={e => setDensity(e.target.value)} />
          <select className="cnet-select" value={dUnit} onChange={e => setDUnit(e.target.value)}>
            {Object.keys(DENSITY).map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </CNetRow>
        <CNetRow label="Volume">
          <input data-testid="mass-volume" className="cnet-input" type="number" value={volume} onChange={e => setVolume(e.target.value)} />
          <select className="cnet-select" value={vUnit} onChange={e => setVUnit(e.target.value)}>
            {Object.keys(VOLUME).map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </CNetRow>
        <CNetActions onCalc={calc} onClear={clear} testId="mass" />
      </div>

      {res.kg != null && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900 mb-3" data-testid="mass-result">Mass = {G(res.kg, 6)} kilogram</div>
          {res.showSteps && (
            <div className="mb-4">
              <div className="font-bold text-slate-900">Steps:</div>
              <div className="ml-2 text-slate-800 leading-7">
                <div>m = V × ρ</div>
                <div className="ml-4">= {volume} {res.vLabel?.split(' [')[0]} × {density} {res.dLabel?.split(' [')[0]}</div>
                <div className="ml-4">= {G(res.kg, 6)} kilogram</div>
              </div>
            </div>
          )}
          <div className="mt-3 font-medium">Mass in other units.</div>
          <table className="data-table mt-2" data-testid="mass-units-table">
            <thead><tr><th>Amount</th><th>Unit</th></tr></thead>
            <tbody>
              {MASS_UNITS.map(([lbl, f]) => (
                <tr key={lbl}><td>{G(res.kg * f, 9)}</td><td>{lbl}</td></tr>
              ))}
            </tbody>
          </table>
        </CNetResult>
      )}
    </CalcShell>
  );
}
